import { c as _c } from "react/compiler-runtime";
import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import { FaroTableEmptyPage } from "@components/common/faro-table/faro-table-empty-page";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { Skeleton } from "@flat-ui";
import { useMembers } from "@hooks/use-members";
import { Box, Stack } from "@mui-wrapper";
import { getMarkupDueDateUrgency } from "@pages/project-details/project-markups/board/project-markups-board-utils";
import { getMarkupAssigneeMember } from "@pages/project-details/project-markups/markup-assignee-utils";
import { MarkupListItemCardMobile } from "@pages/project-details/project-markups/mobile/markup-list-item-card-mobile";
import { getMarkupsDueDateFilterOptions } from "@pages/project-details/project-markups/project-markups-filters/project-markups-filter-list";
import { areMarkupStatusesUsedSelector } from "@store/markups/markups-selector";
import { useAppSelector } from "@store/store-helper";
import { selectedRowIdsSelector } from "@store/table/table-selector";

/** Estimated height of a card, before it is measured, as the tags and the due date are optional */
const ESTIMATED_CARD_HEIGHT = 140;

/** Placeholder of a card while the markups load, with the rows of a card with all its parts */
function MarkupListItemCardSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Stack sx={{
      gap: 1,
      px: 2,
      py: 1.5,
      borderBottom: 1,
      borderColor: "divider"
    }}><Skeleton variant="rounded" width="6rem" height="1.25rem" /><Skeleton width="80%" height="1.25rem" /><Skeleton width="50%" height="1.25rem" /><Skeleton variant="rounded" width="7rem" height="1.25rem" /></Stack>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
/** The markups on small screens, as a virtualized list of cards like the other lists */
export function ProjectMarkupsMobileView(t0) {
  const $ = _c(24);
  const {
    markups,
    isLoading,
    sortKey,
    onMarkupClick,
    bulkActionButtons
  } = t0;
  const {
    companyMembers,
    projectMembers
  } = useMembers();
  const {
    selectedMarkup,
    selectedMarkupId
  } = useMarkupContext();
  const areStatusesUsed = useAppSelector(areMarkupStatusesUsedSelector);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getMarkupsDueDateFilterOptions();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const dueDateFilters = t1;
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  if (!isLoading && markups.length === 0) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <FaroTableEmptyPage />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] !== areStatusesUsed || $[3] !== companyMembers || $[4] !== onMarkupClick || $[5] !== projectMembers || $[6] !== selectedMarkup?.id || $[7] !== selectedMarkupId) {
    t2 = markup => <Box key={markup.id}><MarkupListItemCardMobile markup={markup} assignee={getMarkupAssigneeMember({
        assignee: markup.assignee,
        companyMembers,
        projectMembers
      })} dueDateUrgency={getMarkupDueDateUrgency(markup, dueDateFilters)} shouldShowStatus={areStatusesUsed} isSelected={markup.id === selectedMarkupId || markup.id === selectedMarkup?.id} onClick={onMarkupClick} /></Box>;
    $[2] = areStatusesUsed;
    $[3] = companyMembers;
    $[4] = onMarkupClick;
    $[5] = projectMembers;
    $[6] = selectedMarkup?.id;
    $[7] = selectedMarkupId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== isLoading || $[10] !== markups || $[11] !== sortKey || $[12] !== t2) {
    t3 = <VirtualizedGrid key={sortKey} items={markups} isLoading={isLoading} estimateSize={ESTIMATED_CARD_HEIGHT} shouldMeasureRowHeight={true} renderSkeletonItem={_temp} renderItem={t2} />;
    $[9] = isLoading;
    $[10] = markups;
    $[11] = sortKey;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = selectedRowIds.length;
  let t5;
  if ($[14] !== markups) {
    t5 = markups.map(_temp2);
    $[14] = markups;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== bulkActionButtons || $[17] !== isLoading || $[18] !== selectedRowIds.length || $[19] !== t5) {
    t6 = <FaroTableBulkActions bulkActionsButtons={bulkActionButtons} numberOfSelectedRows={t4} isLoading={isLoading} allRowIds={t5} />;
    $[16] = bulkActionButtons;
    $[17] = isLoading;
    $[18] = selectedRowIds.length;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t3 || $[22] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[21] = t3;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
function _temp2(markup_0) {
  return markup_0.id;
}
function _temp(index) {
  return <Box key={index}><MarkupListItemCardSkeleton /></Box>;
}